import React, { useMemo } from 'react';
import { BellIcon, ClipboardListIcon, UsersIcon, CalendarIcon } from '../icons';
import AdminSubmissionsReview from './AdminSubmissionsReview';
import AdminNotificationsCenter from './AdminNotificationsCenter';
import AdminUserManagement from './AdminUserManagement';
import AdminActivityLog from './AdminActivityLog';
import AdminDeadlineSetter from './AdminDeadlineSetter';

const AdminDashboard = ({
  allUsers,
  allSubmissions,
  deadlines,
  deadlineExtensions,
  onUpdateSubmission,
  onSetSubmissionExclusion,
  onDeleteSubmission,
  onPublishResults,
  onSetDeadline,
  onSetExtension,
  onRemoveExtension,
  onCreateUser,
  onReviewRegistration,
  onUpdateCompany,
  onDeleteCompany,
  onSendCompanyReminder,
  onTestTransactionalEmail,
  onNotify,
  onNav,
  page,
  adminNotifications = [],
  onMarkNotificationRead,
  onResolveNotification,
  onMarkAllNotificationsRead,
}) => {
  const companyUsers = allUsers.filter((u) => u.role === 'company');
  const pendingReviews = allSubmissions.filter((s) => s.status === 'pending' && !s.excludedFromResults).length;
  const extensionCount = Object.values(deadlineExtensions).reduce(
    (acc, record) => acc + Object.keys(record || {}).length,
    0,
  );
  const submittedCompanyIds = useMemo(
    () => new Set(allSubmissions.filter((s) => !s.excludedFromResults).map((s) => s.companyId)),
    [allSubmissions],
  );
  const approvedCompanyIds = useMemo(
    () => new Set(allSubmissions.filter((s) => s.status === 'approved' && !s.excludedFromResults).map((s) => s.companyId)),
    [allSubmissions],
  );
  const pendingCompanyIds = useMemo(
    () => new Set(allSubmissions.filter((s) => s.status === 'pending' && !s.excludedFromResults).map((s) => s.companyId)),
    [allSubmissions],
  );
  const submittedCompanies = submittedCompanyIds.size;
  const approvedCompanies = approvedCompanyIds.size;
  const pendingCompanies = pendingCompanyIds.size;
  const unreadNotifications = adminNotifications.filter((item) => item.status === 'unread').length;

  const adminNavItems = [
    { id: 'admin_activity', label: 'Historial y correos', description: 'Acciones, entregas y errores.', icon: ClipboardListIcon },
    {
      id: 'admin_notifications',
      label: 'Notificaciones',
      description: 'Centraliza solicitudes y eventos de empresas.',
      icon: BellIcon,
      metric: unreadNotifications,
    },
    {
      id: 'admin_submissions',
      label: 'Revisión de envíos',
      description: 'Aprueba cuestionarios y publica resultados.',
      icon: ClipboardListIcon,
      metric: pendingReviews,
    },
    {
      id: 'admin_users',
      label: 'Empresas y usuarios',
      description: 'Da de alta nuevas compañías en minutos.',
      icon: UsersIcon,
      metric: companyUsers.length,
    },
    {
      id: 'admin_deadlines',
      label: 'Plazos y recordatorios',
      description: 'Gestiona fechas límite y prórrogas.',
      icon: CalendarIcon,
      metric: extensionCount,
    },
  ];

  const renderActiveSection = () => {
    switch (page) {
      case 'admin_activity':
        return <AdminActivityLog />;
      case 'admin_submissions':
        return (
          <AdminSubmissionsReview
            allSubmissions={allSubmissions}
            onUpdateSubmission={onUpdateSubmission}
            onSetSubmissionExclusion={onSetSubmissionExclusion}
            onDeleteSubmission={onDeleteSubmission}
            onPublishResults={onPublishResults}
            onNotify={onNotify}
          />
        );
      case 'admin_notifications':
        return (
          <AdminNotificationsCenter
            notifications={adminNotifications}
            onMarkNotificationRead={onMarkNotificationRead}
            onResolveNotification={onResolveNotification}
            onMarkAllNotificationsRead={onMarkAllNotificationsRead}
            onNav={onNav}
          />
        );
      case 'admin_users':
        return (
          <AdminUserManagement
            users={allUsers}
            submissions={allSubmissions}
            onCreateUser={onCreateUser}
            onReviewRegistration={onReviewRegistration}
            onUpdateCompany={onUpdateCompany}
            onDeleteCompany={onDeleteCompany}
            onSendCompanyReminder={onSendCompanyReminder}
            onTestTransactionalEmail={onTestTransactionalEmail}
            onNotify={onNotify}
          />
        );
      case 'admin_deadlines':
        return (
          <AdminDeadlineSetter
            deadlines={deadlines}
            extensions={deadlineExtensions}
            users={allUsers}
            onSetDeadline={onSetDeadline}
            onSetExtension={onSetExtension}
            onRemoveExtension={onRemoveExtension}
            onSendReminder={onSendCompanyReminder}
            onNotify={onNotify}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6">
      {/* Page header */}
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[0.6rem] uppercase text-slate-400 mb-1">Centro Administrativo</p>
          <h1 className="text-xl font-semibold text-slate-900">Panel de operaciones</h1>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {[
          { label: 'Notificaciones nuevas', value: unreadNotifications, helper: 'sin leer', accentColor: 'bg-[#004AAD]' },
          { label: 'Empresas con envíos', value: submittedCompanies, helper: 'han participado', accentColor: 'bg-[#004AAD]' },
          { label: 'Aprobadas', value: approvedCompanies, helper: 'cuentas con aval', accentColor: 'bg-emerald-500' },
          { label: 'Pendientes de revisión', value: pendingCompanies, helper: 'esperando ajustes', accentColor: 'bg-amber-400' },
          { label: 'Prórrogas activas', value: extensionCount, helper: 'extensiones registradas', accentColor: 'bg-slate-300' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl border border-slate-200/70 p-5 shadow-sm">
            <div className={`w-6 h-1 rounded-full ${stat.accentColor} mb-4`} />
            <p className="text-[0.6rem] uppercase text-slate-400 mb-1">{stat.label}</p>
            <p className="text-3xl font-semibold text-slate-900 mb-0.5">{stat.value}</p>
            <p className="text-xs text-slate-400">{stat.helper}</p>
          </div>
        ))}
      </div>

      {/* Section nav */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {adminNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = page === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onNav(item.id)}
              className={`flex items-center gap-4 rounded-xl border p-4 text-left transition-all duration-150 ${
                isActive
                  ? 'bg-[#004AAD] border-[#004AAD] shadow-md shadow-[#004AAD]/15'
                  : 'bg-white border-slate-200 hover:border-[#004AAD]/30 hover:shadow-sm'
              }`}
            >
              <span className={`flex-shrink-0 rounded-lg p-2.5 ${isActive ? 'bg-white/15' : 'bg-slate-100'}`}>
                <Icon className={`h-5 w-5 ${isActive ? 'text-white' : 'text-[#004AAD]'}`} />
              </span>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${isActive ? 'text-white' : 'text-slate-800'}`}>{item.label}</p>
                <p className={`text-xs mt-0.5 leading-snug ${isActive ? 'text-white/65' : 'text-slate-400'}`}>{item.description}</p>
              </div>
              {item.metric > 0 && (
                <span className={`flex-shrink-0 min-w-[22px] h-[22px] rounded-full text-xs font-bold flex items-center justify-center px-1 ${
                  isActive ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-700'
                }`}>
                  {item.metric}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div>{renderActiveSection()}</div>
    </div>
  );
};

export default AdminDashboard;
